import {Flame, Gift, SprayCan, TicketPercent} from "@lucide/vue";
import {FunctionalComponent} from "vue";
import {
    GiftAgeBand,
    GiftPalette,
    GiftSendHour,
    GiftSex,
    GiftValidityDays,
    VoucherBlockedReason
} from "@/birthday-gift/interfaces";

/**
 * The hours at which automatic gifts may be sent.
 */
export const SEND_HOURS: GiftSendHour[] = [8, 9, 10, 14];

/**
 * The voucher validity durations, in days.
 */
export const VALIDITY_DAYS: GiftValidityDays[] = [7, 15, 30];

/**
 * Ready-made gift names suggested under the name field.
 */
export const GIFT_NAME_EXAMPLES = [
    'Une bougie Roger & Gallet offerte',
    '10 € de bon d\'achat sur la gamme Nuxe',
    'Un produit Caudalie offert',
];

export interface GiftPaletteDefinition {
    label: string;
    tint: string;
    iconColor: string;
    icon: FunctionalComponent;
}

/**
 * The 4 visual palettes a pharmacist can pick for a gift.
 */
export const GIFT_PALETTES: Record<GiftPalette, GiftPaletteDefinition> = {
    beige: {label: 'Beige', tint: '#f6efe6', iconColor: '#b98a52', icon: Flame},
    orange: {label: 'Orange', tint: '#fff2e2', iconColor: '#d98b2b', icon: TicketPercent},
    vert: {label: 'Vert', tint: '#eaf6ee', iconColor: '#4a8f63', icon: SprayCan},
    violet: {label: 'Violet', tint: '#EEECF7', iconColor: '#3513e2', icon: Gift},
};

export const AGE_BAND_LABELS: Record<GiftAgeBand, string> = {
    'all': 'Tous les âges',
    '18-30': '18–30 ans',
    '31-45': '31–45 ans',
    '46-60': '46–60 ans',
    '60+': '60 ans et +',
};

/**
 * Why a voucher cannot be sent, in the counter team's words.
 */
export const BLOCKED_REASON_LABELS: Record<VoucherBlockedReason, string> = {
    no_valid_phone: 'Pas de mobile',
    no_matching_gift: 'Aucun cadeau',
    auto_conflict: 'Conflit de profils',
    no_credit: 'Crédits épuisés',
};

export const SEX_LABELS: Record<GiftSex, string> = {
    all: 'Tous',
    f: 'Femmes',
    m: 'Hommes',
};

/**
 * The age bands bounds, disjoint by design: 60 belongs to 46-60
 * and « 60+ » starts at 61 (mirrors the API contract).
 */
const AGE_BAND_BOUNDS: Record<GiftAgeBand, [number, number]> = {
    'all': [0, 200],
    '18-30': [18, 30],
    '31-45': [31, 45],
    '46-60': [46, 60],
    '60+': [61, 200],
};

/**
 * Whether two gifts target overlapping patient profiles, which the API
 * forbids between two automatic gifts.
 */
export function giftProfilesOverlap(
    first: {age_band: GiftAgeBand, sex: GiftSex},
    second: {age_band: GiftAgeBand, sex: GiftSex}
): boolean {
    const [firstMin, firstMax] = AGE_BAND_BOUNDS[first.age_band];
    const [secondMin, secondMax] = AGE_BAND_BOUNDS[second.age_band];

    const agesOverlap = firstMin <= secondMax && secondMin <= firstMax;
    const sexesOverlap = first.sex === 'all' || second.sex === 'all' || first.sex === second.sex;

    return agesOverlap && sexesOverlap;
}

/**
 * Human label of a gift patient profile (sex · age band).
 */
export function giftProfileLabel(ageBand: GiftAgeBand, sex: GiftSex): string {
    if (ageBand === 'all' && sex === 'all') {
        return 'Tous les patients';
    }

    if (ageBand === 'all') {
        return SEX_LABELS[sex];
    }

    if (sex === 'all') {
        return AGE_BAND_LABELS[ageBand];
    }

    return `${SEX_LABELS[sex]} · ${AGE_BAND_LABELS[ageBand]}`;
}
